'use client';

import { useTheme } from '@emotion/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTimer } from 'react-timer-hook';
import { DateTime } from 'luxon';
import RedMoneySVG from '../../public/img/redMoney.svg';
import WhenSVG from '../../public/img/when.svg';
import LocationSVG from '../../public/img/location.svg';
import ViewsSVG from '../../public/img/views2.svg';
import RatingSVG from '../../public/img/star-outline.svg';
import PushkinSVG from '../../public/img/pushkin.svg';
import { getTitleForTimer } from '../../utils/getTitleForTimer';
import { eventTypes } from '../../constants/events';
import {
  DescriptionItem,
  LabelFeatureListItem,
  ActionButton,
  ActionLabelItem,
} from './styled';
import { Picture } from '../picture/picture';
import { Link } from '../styled/Link';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { getBuyButtonText, useSendMetricsFromButton } from '../../utils/event';
import { Action } from '../../models';
import FavoriteButton from '../favorite/FavoriteButton';
import { FavoriteKinds } from '../../models/favorite';
import FavoriteButtonCardWrapper from '../favorite/FavoriteButtonCardWrapper';
import { ACTION_TYPES } from '../../models/action-types';
import { extractUtmParams } from '../../utils/utm';
import { objectToSearchParams } from '../../utils/objectToSearchParams';
import { countdownInit } from '../../utils/countdownInit';
import { getPriceWithCurrency } from '../../utils/getPriceWithCurrency';
import { splitPriceByThree } from '../../utils/splitPriceByThree';
import { formatTimer } from '../../utils/formatTimer';
import { getBenefit } from '../../utils/getBenefit';
import { withSuspense } from '../../utils/withSuspense';

type ITimerData = {
  showTimer: boolean;
  startDate: Date | null;
  endDate: Date | null;
  title: string | null;
};

function ActionCardColumnInner({ item }: { item: Action }) {
  const theme = useTheme();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [timerData, setTimerData] = useState<ITimerData>({
    showTimer: false,
    startDate: null,
    endDate: null,
    title: null,
  });

  const { seconds, minutes, hours, days, restart } = useTimer({
    expiryTimestamp: timerData.endDate ?? new Date(),
    onExpire: () => console.warn('onExpire called'),
  });

  const [progressValue, setProgressValue] = useState(0);
  const seoAnalyticsFromButton = useSendMetricsFromButton();
  const utmParams = extractUtmParams(searchParams);

  useEffect(() => {
    const newTimerData: ITimerData = {
      showTimer: false,
      startDate: null,
      endDate: null,
      title: getTitleForTimer(item.countdown_type),
    };

    const dataPrepareForTimer = countdownInit(
      [DateTime.fromISO(item.countdown_datetime)],
      item.countdown_type
    );

    if (dataPrepareForTimer) {
      newTimerData.showTimer = true;
      newTimerData.endDate = new Date(Date.now() + dataPrepareForTimer.date);
      newTimerData.startDate = DateTime.fromMillis(
        Date.now() + dataPrepareForTimer.date
      )
        .minus({
          weeks: 1,
        })
        .toJSDate();
    }

    if (newTimerData.showTimer) {
      const a = (Date.now() - Number(newTimerData.startDate?.getTime())) * 100;
      const b =
        Number(newTimerData.endDate?.getTime()) -
        Number(newTimerData.startDate?.getTime());
      const newProgressVal = Math.round(a / b);
      setProgressValue(newProgressVal);

      if (newTimerData.endDate) {
        restart(newTimerData.endDate);
      }
    }

    setTimerData(newTimerData);
  }, [item]);

  return (
    <div
      // actions-column__item actions-column__item_compilation
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      css={{
        ...theme.mixins.col(1),
        marginBottom: 64,
        [DEVICE_SCREENS_MAX_STRING.xl]: {
          ...theme.mixins.col(1),
          marginBottom: 32,
        },
        [DEVICE_SCREENS_MAX_STRING.lg]: {
          ...theme.mixins.col(1),
        },
        [DEVICE_SCREENS_MAX_STRING.md]: {
          ...theme.mixins.col(1),
          marginBottom: 16,
        },
        [DEVICE_SCREENS_MAX_STRING.sm]: {
          ...theme.mixins.col(1),
        },
      }}
    >
      <div
        // action-card-column
        css={{
          borderRadius: 4,
          backgroundColor: theme.colors.white,
          position: 'relative',
          top: 0,
          transition: 'all .3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.constants.boxShadow,

            [DEVICE_SCREENS_MAX_STRING.md]: {
              transform: 'none',
              boxShadow: 'none',
            },
          },
        }}
      >
        <Link
          // action-card-column__image-wrapper
          href={['', item.city.url, 'actions', item.slug, ''].join('/')}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          css={{
            position: 'relative',
            width: 472,
            display: 'block',
            overflow: 'hidden',
            borderRadius: '4px 0 0 4px',
            '&::before': {
              content: "''",
              display: 'block',
              paddingTop: 257,
            },
            [DEVICE_SCREENS_MAX_STRING.xl]: {
              position: 'relative',
              width: '100%',

              '&::before': {
                content: "''",
                display: 'block',
                paddingTop: '75%',
              },
            },
          }}
        >
          <div
            // action-card__label-block
            css={{
              position: 'absolute',
              top: 30,
              left: 0,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'row',
              fontFamily: 'Golos',
            }}
          >
            {!item.instead_price &&
              !item.filter_price &&
              item.type !== eventTypes.restaurant && (
                <ActionLabelItem
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  css={{
                    borderRadius: item.pushkin_card_payment
                      ? '0'
                      : '0 4px 4px 0',
                    backgroundColor: theme.colors.red,
                    background: 'rgba(0, 102, 204, 0.80)',
                  }}
                >
                  бесплатно
                </ActionLabelItem>
              )}
            {!!item.discount && (
              <ActionLabelItem
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                css={{
                  borderRadius: item.pushkin_card_payment ? '0' : '0 4px 4px 0',
                  backgroundColor: 'rgba(244, 67, 54, 0.80)',
                }}
              >
                −{item.discount}%&nbsp;скидка
              </ActionLabelItem>
            )}
            {!!item.pushkin_card_payment && (
              <ActionLabelItem
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                css={{
                  background: '#FFFFFF',
                  opacity: '0.8',
                  borderRadius: '0px 4px 4px 0px',
                  height: '34px',
                  width: '36px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  svg: {
                    transform: 'scale(0.8)',
                  },
                }}
              >
                <PushkinSVG height='34px' width='36px' />
              </ActionLabelItem>
            )}
          </div>
          {item.countdown_type &&
            item.countdown_type !== 'unset' &&
            timerData.showTimer && (
              <div
                css={{
                  width: '100%',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: '#fff',
                  padding: '6px 0',
                  zIndex: 10,
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                }}
              >
                <div
                  css={{
                    width: '90%',
                    margin: '0 auto',
                  }}
                >
                  <p css={{ textAlign: 'center', fontSize: 13 }}>
                    {timerData.title}{' '}
                    {formatTimer(days, hours, minutes, seconds)}
                  </p>
                  <div
                    css={{
                      width: '100%',
                      height: 3,
                      background: '#fff',
                      borderRadius: 2,
                      marginTop: 5,
                    }}
                  >
                    <div
                      css={{
                        height: '100%',
                        width: `${progressValue}%`,
                        background: 'rgb(244, 67, 54)',
                        borderRadius: 2,
                        marginLeft: -1,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          {item.image && (
            <div
              // action-card-column__image
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                overflow: 'hidden',

                '& > img': {
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  maxWidth: '100%',
                },
              }}
            >
              <Picture
                initWhiteBlackFilter={item.not_season}
                src={item.image}
                title={item.title_short}
                alt={item.title}
              />
            </div>
          )}
        </Link>
        <ul
          // action-card__label-container-sub
          css={{
            padding: 0,
            margin: 0,
            position: 'absolute',
            top: '-7px',
            left: '8px',
            right: '8px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
          }}
        >
          {item.not_season && (
            <LabelFeatureListItem text='Не сезон' title='Не сезон' />
          )}
          {item.best_reviews && (
            <LabelFeatureListItem
              text='лучшие отзывы'
              title='Пользователи высоко оценивают мероприятие'
            />
          )}
          {item.hit && (
            <LabelFeatureListItem
              text='хит'
              title='Пользователи часто просматривают это мероприятие'
            />
          )}
          {item.first_time && (
            <LabelFeatureListItem
              text='впервые'
              title='Событие размещено на сайте впервые'
            />
          )}
          {item.premiere && (
            <LabelFeatureListItem
              text='премьера'
              title='Премьерные показы мероприятия'
            />
          )}
          {item.prepayment && (
            <LabelFeatureListItem
              text='предоплата'
              title='Онлайн вы вносите только предоплату'
            />
          )}
        </ul>
        <FavoriteButtonCardWrapper compilationCard>
          <FavoriteButton
            kind={FavoriteKinds.event}
            id={item.id}
            size='small'
            variant='red-outline'
          />
        </FavoriteButtonCardWrapper>
        <div
          // action-card-column__info
          css={{
            position: 'absolute',
            top: 0,
            left: '472px',
            right: 0,
            bottom: 0,

            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: 20,
            [DEVICE_SCREENS_MAX_STRING.xl]: {
              position: 'static',
              padding: '20px 20px 12px',
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              padding: '14px 14px 8px',
            },
          }}
        >
          {item.reservation_count_text && (
            <div
              // action-card-column__description_reservation_count_text
              css={{
                fontFamily: 'Golos',
                fontSize: 14,
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0em',
                color: theme.colors.red,
              }}
            >
              {item.reservation_count_text}
            </div>
          )}
          <Link
            // action-card-column__title
            href={['', item.city.url, 'actions', item.slug, ''].join('/')}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            css={{
              fontFamily: 'Montserrat',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '22px',
              letterSpacing: '0em',
              width: 'calc(100% - 130px)',
              textDecoration: 'none',
              display: 'inline-block',
              '&:hover': {
                color: theme.colors.black,
              },
              [DEVICE_SCREENS_MAX_STRING.xl]: {
                width: '100%',
              },
              [DEVICE_SCREENS_MAX_STRING.md]: {
                width: '100%',
              },
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                maxHeight: 'initial',
                marginBottom: '10px',
                minHeight: '69px',
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            {item.title}
          </Link>
          {!!item.short_description && (
            <div
              css={{
                fontFamily: 'Golos',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0em',
                paddingRight: 40,
                paddingBottom: 5,
                '> div': {
                  maxHeight: 70,
                  overflow: 'hidden',
                },
                [DEVICE_SCREENS_MAX_STRING.xl]: {
                  paddingRight: 0,
                  height: 'auto',
                  paddingBottom: 10,
                },
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  paddingRight: 0,
                  height: 'auto',
                  paddingBottom: 10,
                  '> div': {
                    maxHeight: 80,
                    overflow: 'hidden',
                  },
                },
                [DEVICE_SCREENS_MAX_STRING.sm]: {
                  paddingRight: 0,
                  height: 'auto',
                  paddingBottom: 10,
                  '> div': {
                    maxHeight: 160,
                    overflow: 'hidden',
                  },
                },
              }}
            >
              <div>
                {item.short_description.length > 200
                  ? item.short_description.slice(0, 200).concat('...')
                  : item.short_description}
              </div>
            </div>
          )}
          <div
            // action-card-column__description
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 15,
              color: theme.colors.black,
              fontSize: 13,
              fontWeight: 500,
              [DEVICE_SCREENS_MAX_STRING.xl]: {
                flexDirection: 'column',
                gap: 0,
              },
              '> div > div:not(:last-child) ': {
                marginBottom: 5,
              },
            }}
          >
            <div>
              {item.type !== eventTypes.restaurant && !!item.dates_txt && (
                <DescriptionItem
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  css={{
                    '& span:not(:last-child):after': {
                      content: '", "',
                    },
                  }}
                >
                  {item.has_further_dates && item.dates_txt ? (
                    <>
                      <WhenSVG css={{ width: '16px', height: '16px' }} />
                      {item.dates_txt}
                    </>
                  ) : (
                    <span
                      css={{
                        display: 'flex',
                        gap: 8,
                      }}
                    >
                      {item.date_unknown && (
                        <>
                          <WhenSVG css={{ width: '16px', height: '16px' }} />
                          <p>Дата уточняется</p>
                        </>
                      )}
                    </span>
                  )}
                </DescriptionItem>
              )}
              {item.type !== eventTypes.restaurant &&
                (!!item.filter_price || !!item.price_amount) && (
                  <DescriptionItem>
                    <RedMoneySVG css={{ width: '16px', height: '16px' }} />
                    {!!item.instead_price && !!item.filter_price && (
                      <span
                        title={`Ваша экономия ${getPriceWithCurrency(
                          getBenefit(
                            item.instead_price,
                            item.type === 'tsnext_widget' ||
                              item.type === 'partner_widget'
                              ? Number(item.price_amount)
                              : Number(item.filter_price)
                          ),
                          item.price_currency
                        )}`}
                      >
                        <span>
                          от{' '}
                          {getPriceWithCurrency(
                            item.type === 'tsnext_widget' ||
                              item.type === 'partner_widget'
                              ? Number(item.price_amount)
                              : Number(item.filter_price),
                            item.price_currency
                          )}{' '}
                        </span>
                        <meta
                          itemProp='priceCurrency'
                          content={item.price_currency}
                        />
                        вместо {splitPriceByThree(item.instead_price)}
                      </span>
                    )}
                    {!!item.filter_price && !item.instead_price && (
                      <span>
                        {`от ${getPriceWithCurrency(
                          item.type === 'tsnext_widget' ||
                            item.type === 'partner_widget'
                            ? Number(item.price_amount)
                            : Number(item.filter_price),
                          item.price_currency
                        )}`}
                      </span>
                    )}
                  </DescriptionItem>
                )}
              {item.place_title && (
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                <DescriptionItem css={{ marginRight: 16 }}>
                  <LocationSVG
                    fill={theme.colors.red}
                    css={{ width: '16px', height: '16px' }}
                  />
                  <span>{item.place_title}</span>
                </DescriptionItem>
              )}
            </div>
            <div
              css={{
                marginTop: 'auto',
                position: 'relative',
                bottom: 0,
                right: 0,
                [DEVICE_SCREENS_MAX_STRING.xl]: {
                  position: 'static',
                  padding: '16px 0',
                },
                [DEVICE_SCREENS_MAX_STRING.sm]: {
                  position: 'static',
                  padding: '16px 0',
                },
              }}
            >
              {item.age_category && (
                <div
                  css={{
                    fontFamily: 'Golos',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    fontSize: 13,
                    textAlign: 'right',
                    marginBottom: 16,
                    color: theme.colors.grayStrong,
                    [DEVICE_SCREENS_MAX_STRING.xl]: {
                      display: 'none',
                    },
                  }}
                >
                  {item.age_category}
                </div>
              )}
              <ActionButton
                bookButton={item.type !== ACTION_TYPES.FREE}
                onButtonClick={() => {
                  seoAnalyticsFromButton.onSend(item);
                  router.push(
                    `/${item.city.url}/actions/${
                      item.slug
                    }/?${objectToSearchParams({
                      order: '',
                      ...utmParams.utmObject,
                    }).toString()}`
                  );
                }}
              >
                {getBuyButtonText(item, false, true)}
              </ActionButton>
            </div>
          </div>
          <div
            // action-card-column__misc
            css={{
              position: 'absolute',
              right: 20,
              top: 20,

              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: theme.colors.grayStrong,
              fontFamily: 'Golos',
              fontSize: 12,
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '16px',
              letterSpacing: '0.01em',
              gap: 18,
              [DEVICE_SCREENS_MAX_STRING.xl]: {
                position: 'static',
                padding: 0,
                width: '100%',
              },
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                position: 'static',
                padding: 0,
                width: '100%',
              },
            }}
          >
            {item.comments_avg_rating > 0 && (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '1.3',
                  height: 16,
                  marginLeft: 18,
                  gap: 5,
                  svg: {
                    flex: '0 0 auto',
                  },
                }}
              >
                <RatingSVG css={{ width: '16px', height: '16px' }} />
                <span>{item.comments_avg_rating.toFixed(1)}</span>
              </div>
            )}
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                height: '16px',
                fontFamily: 'Golos',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '16px',
                letterSpacing: '0.01em',
                color: theme.colors.grayStrong,
                svg: {
                  flex: '0 0 auto',
                  marginRight: '5px',
                },
              }}
            >
              <ViewsSVG
                fill={theme.colors.grayStrong}
                css={{ width: '16px', height: '16px' }}
              />
              <span>{item.views}</span>
            </div>
            {item.age_category && (
              <div
                css={{
                  fontFamily: 'Golos',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: 13,
                  textAlign: 'right',
                  marginLeft: 'auto',
                  display: 'none',
                  color: theme.colors.grayStrong,
                  [DEVICE_SCREENS_MAX_STRING.xl]: {
                    display: 'block',
                  },
                }}
              >
                {item.age_category}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export const ActionCardColumn = withSuspense(ActionCardColumnInner);
